/* @flow */

import { useState, useEffect, useCallback } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import PropTypes from 'prop-types';

// Components
import HeartComponent from './HeartIconWrapper';
import type { Heart_heartPortfolioItems } from './__generated__/Heart_heartPortfolioItems.graphql'; // eslint-disable-line camelcase
import type { Heart_viewer } from './__generated__/Heart_viewer.graphql'; // eslint-disable-line camelcase

// Helpers
import get from 'lodash.get';
import { ADD, REMOVE } from './ModifyPortfolioItems';
import { mobile as mobileCheck } from 'dibs-client-check';

// Mutations
import ModifyPortfolioItems from './ModifyPortfolioItems';

// Types
import type { favoriteItem as favoriteItemType } from './types';
import type { RelayProp } from 'react-relay/legacy';

type HeartProps = {
    className: string,
    authModalShow: (authModalShowInfo?: () => void) => void,
    forceFavoriteItem?: boolean,
    itemId: string,
    onError?: (e: ?Error) => void,
    onFavorited: () => void,
    onHeartClick: (favoriteItemInfo?: (favoriteItemType) => void) => void,
    onUnFavorited: () => void,
    doRefetch: () => Promise<void>,
    relay: RelayProp,
    type: 'outline' | 'button',
    userId: string,
    heartPortfolioItems?: Heart_heartPortfolioItems, // eslint-disable-line camelcase
    viewer?: Heart_viewer, //eslint-disable-line camelcase
};

function useForceFavoriteItem({ forceFavoriteItem, onHeartClick, portfolioId, favoriteItem }) {
    const [hasForcedFavoriteItem, setHasForcedFavoriteItem] = useState(false);
    if (!hasForcedFavoriteItem && forceFavoriteItem && onHeartClick) {
        setHasForcedFavoriteItem(true);
        onHeartClick(args => {
            if (!portfolioId && args.portfolioId) {
                favoriteItem({ portfolioId: args.portfolioId, userId: args.userId });
            }
        });
    }
}

export function Heart(props: HeartProps) {
    const {
        authModalShow,
        heartPortfolioItems,
        itemId,
        userId,
        viewer,
        relay,
        doRefetch,
        onFavorited,
        onUnFavorited,
        onError,
        isStrokeWidthHeavy,
    } = props;
    const isFavorited = !!get(heartPortfolioItems, 'length');
    const [isAnimated, setIsAnimated] = useState(false);
    const [isFilled, setIsFilled] = useState(isFavorited);
    const [useHoverClass, setUseHoverClass] = useState(false);
    const [isLoginFlowInProgress, setIsLoginFlowInProgress] = useState(
        userId && !heartPortfolioItems
    );
    const [hasPendingNetworkRequest, setHasPendingNetworkRequest] = useState(false);
    const portfolioId = get(viewer, 'userPortfolios.edges[0].node.serviceId');

    useEffect(() => {
        // apply hoverClass if user device doesn't support touch
        const hasTouch = mobileCheck.hasTouch();
        if (!useHoverClass && !hasTouch) {
            setUseHoverClass(true);
        }
    }, [useHoverClass, setUseHoverClass]);

    useEffect(() => {
        if (userId && !heartPortfolioItems) {
            setHasPendingNetworkRequest(true);
        }
    }, [userId, heartPortfolioItems]);

    const favoriteItem = useCallback(
        args => {
            if (args.portfolioId && args.userId) {
                const action = !isFavorited || isLoginFlowInProgress ? ADD : REMOVE;
                setIsAnimated(true);
                setHasPendingNetworkRequest(true);
                setIsFilled(action === ADD);

                const portfolioItemIds = (heartPortfolioItems || [])
                    .map(heart => heart.portfolioItemId)
                    .filter(Boolean);

                ModifyPortfolioItems.commit(relay.environment, {
                    action,
                    itemsIds: [itemId],
                    portfolioItemsIds: action === REMOVE ? portfolioItemIds : null,
                    portfolioId: args.portfolioId,
                    userId: args.userId,
                    onCompleted: () => {
                        if (action === ADD && onFavorited) {
                            onFavorited();
                        } else if (action === REMOVE && onUnFavorited) {
                            onUnFavorited();
                        }
                        doRefetch().then(() => {
                            setHasPendingNetworkRequest(false);
                        });
                    },
                    onError: error => {
                        if (onError) {
                            onError(error);
                        }
                        setIsFilled(!isFilled);
                        setHasPendingNetworkRequest(false);
                    },
                });
            } else {
                setIsLoginFlowInProgress(true);
                authModalShow();
            }
        },
        [
            authModalShow,
            doRefetch,
            heartPortfolioItems,
            isFavorited,
            isFilled,
            isLoginFlowInProgress,
            itemId,
            onError,
            onFavorited,
            onUnFavorited,
            relay.environment,
        ]
    );

    function handleHeartClick(e: SyntheticEvent<*>) {
        e.preventDefault();

        if (!hasPendingNetworkRequest) {
            if (props.onHeartClick) {
                props.onHeartClick(favoriteItem);
            } else {
                favoriteItem({ portfolioId, userId });
            }
        }
    }

    // Do not use forceFavoriteItem unless all props fetched in this component's
    // relay container are fetched from an external higher-order component and can be
    // passed along to this component as props
    const { forceFavoriteItem, onHeartClick } = props;
    useForceFavoriteItem({ forceFavoriteItem, onHeartClick, portfolioId, favoriteItem });

    useEffect(() => {
        if (isLoginFlowInProgress && !!userId && !!portfolioId) {
            favoriteItem({ portfolioId, userId });
            setIsLoginFlowInProgress(false);
        }
    }, [favoriteItem, isLoginFlowInProgress, portfolioId, userId]);

    useEffect(() => {
        if (!hasPendingNetworkRequest && isFavorited !== isFilled) {
            setIsFilled(isFavorited);
        }
    }, [hasPendingNetworkRequest, isFavorited, isFilled]);

    return (
        <HeartComponent
            className={props.className}
            buttonClass={props.buttonClass}
            isFavorited={isFavorited}
            isFilled={isAnimated || isFilled}
            isAnimated={isAnimated}
            isStrokeWidthHeavy={isStrokeWidthHeavy}
            useHoverClass={useHoverClass}
            onAnimationEnd={() => setIsAnimated(false)}
            onClick={handleHeartClick}
            type={props.type || 'outline'}
        />
    );
}

Heart.propTypes = {
    className: PropTypes.string,
    buttonClass: PropTypes.string,
    authModalShow: PropTypes.func.isRequired,
    isStrokeWidthHeavy: PropTypes.bool,
    heartPortfolioItems: PropTypes.array,
    forceFavoriteItem: PropTypes.bool,
    itemId: PropTypes.string.isRequired,
    doRefetch: PropTypes.func.isRequired,
    onError: PropTypes.func,
    onFavorited: PropTypes.func,
    onHeartClick: PropTypes.func,
    onUnFavorited: PropTypes.func,
    portfolioId: PropTypes.string,
    relay: PropTypes.object,
    type: PropTypes.oneOf(['outline', 'button']),
    userId: PropTypes.string,
};

export default createFragmentContainer(Heart, {
    heartPortfolioItems: graphql`
        fragment Heart_heartPortfolioItems on PortfolioItemV2 @relay(plural: true) {
            portfolioId
            portfolioItemId
        }
    `,
    viewer: graphql`
        fragment Heart_viewer on Viewer
        @argumentDefinitions(
            loadPortfolioData: { type: "Boolean!", defaultValue: false }
            userIds: { type: "[String]", defaultValue: [] }
        ) {
            userPortfolios: portfolios(first: 1, portfolioTypes: [HEART], userIds: $userIds)
            @include(if: $loadPortfolioData) {
                edges {
                    node {
                        serviceId
                    }
                }
            }
        }
    `,
});
